/**
 * HeroVideoBackground Component
 * 
 * Extracted video background with dark overlay for the hero section.
 * Respects reduced motion preferences using native CSS media query.
 * Uses native matchMedia instead of framer-motion to reduce bundle size.
 */

import { useEffect, useState, useRef } from "react";

interface HeroVideoBackgroundProps {
  videoSrc?: string;
  overlayOpacity?: string;
}

/**
 * Native hook to detect reduced motion preference.
 * Replaces framer-motion's useReducedMotion to reduce critical bundle size.
 * Optimized to avoid unnecessary re-renders on mount.
 */
const useNativeReducedMotion = () => {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  });

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    
    const handler = (event: MediaQueryListEvent) => {
      setPrefersReducedMotion(event.matches);
    };
    
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  return prefersReducedMotion;
};

export const HeroVideoBackground = ({ 
  videoSrc = "/videos/hero-background.mp4",
  overlayOpacity = "bg-black/50"
}: HeroVideoBackgroundProps) => {
  const shouldReduceMotion = useNativeReducedMotion();
  const videoRef = useRef<HTMLVideoElement>(null);

  // Force autoplay on mount - iOS Safari often ignores autoPlay attribute
  // Also handles reduced motion by pausing after play attempt
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (shouldReduceMotion) {
      video.pause();
      return;
    }

    // iOS Safari requires explicit play() call even with autoPlay attribute
    // The play() promise handles cases where autoplay is blocked
    const playVideo = async () => {
      try {
        await video.play();
      } catch (error) {
        // Autoplay was prevented - this is expected on some browsers
        // Video will show first frame as fallback
        console.debug('Video autoplay prevented:', error);
      }
    };

    playVideo();
  }, [shouldReduceMotion]);

  return (
    <div className="absolute inset-0 z-0">
      {/* 
        CRITICAL for mobile autoplay:
        - autoPlay: must be HTML attribute (not conditional) for iOS/Android
        - muted: required for autoplay on all mobile browsers
        - playsInline: prevents iOS from forcing fullscreen
        Video will be paused via useEffect if reduced motion is preferred
      */}
      {/* preload="none" prevents video from competing with LCP resources */}
      <video
        ref={videoRef}
        autoPlay
        muted
        loop
        playsInline
        preload="none"
        className="absolute w-full h-full object-cover"
        aria-hidden="true"
        onError={(e) => {
          // Gracefully hide video on error to prevent console noise
          (e.target as HTMLVideoElement).style.display = 'none';
        }}
      >
        <source src={videoSrc} type="video/mp4" />
      </video>
      {/* Dark overlay for text readability */}
      <div className={`absolute inset-0 ${overlayOpacity}`} />
    </div>
  );
};
